import { useState, useRef, useEffect } from "react";
import { Volume2, RefreshCw, Eraser } from "lucide-react";
import { toHiragana } from "wanakana";
import { speak } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import StrokeOrderSVG from "@/components/app/StrokeOrderSVG";

function TracePad({ char, t }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const clear = () => {
    const c = canvasRef.current;
    if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };
  useEffect(clear, [char]);

  const point = (e, c) => {
    const r = c.getBoundingClientRect();
    const pt = "touches" in e ? e.touches[0] : e;
    return [(pt.clientX - r.left) * (c.width / r.width), (pt.clientY - r.top) * (c.height / r.height)];
  };
  const start = (e) => {
    drawing.current = true;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(...point(e, c));
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#FF80AB"; ctx.lineWidth = 9; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineTo(...point(e, c));
    ctx.stroke();
  };
  const end = () => { drawing.current = false; };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="font-extrabold text-sm text-ink">{t("traceIt")}</div>
        <button data-testid="clear-trace-btn" onClick={clear} className="kawaii-btn kawaii-btn-sm kawaii-btn-ghost !text-xs">
          <Eraser className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("clear")}
        </button>
      </div>
      <div className="relative rounded-2xl bg-white/90 border border-white shadow-soft aspect-square overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="font-jp text-[9rem] text-ink/10 leading-none">{char}</span>
        </div>
        <div className="absolute inset-4 border border-dashed border-ink/15 rounded-xl" />
        <div className="absolute inset-x-4 top-1/2 border-t border-dashed border-ink/15" />
        <div className="absolute inset-y-4 left-1/2 border-l border-dashed border-ink/15" />
        <canvas ref={canvasRef} width={320} height={320} className="w-full h-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="trace-canvas" />
      </div>
    </div>
  );
}

export default function CharacterDetail({ item, system }) {
  const { t, kanjiMeaning } = useLang();
  const [animKey, setAnimKey] = useState(0);
  if (!item) return null;

  const isKanji = system === "kanji";
  const label = isKanji ? kanjiMeaning(item) : item.romaji;
  const onHira = isKanji && item.onyomi && item.onyomi !== "-" ? toHiragana(item.onyomi, { passRomaji: false }) : null;
  const kunHira = isKanji && item.kunyomi && item.kunyomi !== "-" ? toHiragana(item.kunyomi.replace(/[()]/g, ""), { passRomaji: false }) : null;
  const chips = isKanji
    ? [`${item.strokes} strokes`, `JLPT ${item.level}`]
    : [`Row: ${item.row}`, item.type && item.type !== "base" ? item.type : null].filter(Boolean);

  return (
    <div className="kawaii-card p-5 sm:p-6" data-testid="character-detail">
      <div className="relative rounded-3xl bg-gradient-to-br from-white via-yuzu/20 to-sakura/20 aspect-square flex items-center justify-center overflow-hidden" data-testid="character-hero">
        <StrokeOrderSVG char={item.char} size={300} replayKey={animKey} />
        <button data-testid="replay-stroke-btn" onClick={() => setAnimKey((k) => k + 1)}
          className="absolute bottom-3 right-3 kawaii-btn kawaii-btn-sm" title={t("replay")}>
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.5} /> {t("replay")}
        </button>
        <span className="absolute top-3 left-3 kawaii-chip !text-[10px]">KanjiVG</span>
      </div>

      <div className="mt-5 flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow !text-ink-soft mb-1">{t("reading")}</div>
          <div className="font-display text-3xl sm:text-4xl text-ink" data-testid="character-label">
            <span className="reading-highlight">{label}</span>
          </div>
        </div>
        <button data-testid="play-audio-btn" onClick={() => speak(item.char)} className="kawaii-btn kawaii-btn-primary !p-3.5 !rounded-full shrink-0" title={t("playPron")}>
          <Volume2 className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

      {isKanji && (onHira || kunHira) && (
        <div className="mt-4 space-y-2" data-testid="furigana-block">
          {onHira && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="kawaii-chip bg-sakura/40">音</span>
              <span className="font-jp text-xl text-ink"><span className="reading-highlight pink">{onHira}</span></span>
              <span className="text-xs text-ink-soft uppercase font-bold">{item.onyomi}</span>
            </div>
          )}
          {kunHira && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="kawaii-chip bg-matcha/60">訓</span>
              <span className="font-jp text-xl text-ink"><span className="reading-highlight mint">{kunHira}</span></span>
              <span className="text-xs text-ink-soft uppercase font-bold">{item.kunyomi}</span>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {chips.map((s) => <span key={s} className="kawaii-chip">{s}</span>)}
      </div>

      <div className="mt-6">
        <TracePad char={item.char} t={t} />
      </div>
    </div>
  );
}
